import { HubGridViewLayout } from "client/Features/Hub/HubGridViewLayout/HubGridViewLayout";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ObjectWithClientId } from "client/Misc/clientId";
import { ReactElement, useMemo } from "react";
import { getLinkedDocumentId } from "../../useAddDocuments";
import { localize } from "client_legacy/ndaw/culture";
import { InputValidationResultKind } from "client/Validation";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LinkedDocumentItem } from "../../LinkedDocumentItem";
import styles from "./CommunityPostComposerLinkedDocuments.module.less";
import { setTrackingId } from "client/Misc/utils";
import { LinkedDocumentFormData } from "../../common";
import { ValidationError } from "client/Components";

export type CommunityPostComposerLinkedDocumentsProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<LinkedDocumentFormData>[]>;
	isTouch: boolean;
	isMobile: boolean;
	isSharepointEnabled: boolean;
	isWebDavAvailable: boolean;
	onOpenDocument: (document: LinkedDocumentFormData) => void;
};

export const CommunityPostComposerLinkedDocuments = (props: CommunityPostComposerLinkedDocumentsProps): ReactElement => {
	const {
		attachable,
		isTouch,
		isMobile,
		isSharepointEnabled,
		isWebDavAvailable,
		onOpenDocument
	} = props;

	const removeHandler = (index: number): void => {
		attachable
			.reducer
			.removeAt(index);

		attachable
			.validation
			.triggerInputFinished();
	};

	const hasError: boolean = attachable
		.validation
		.getValidationState()
		.kind === InputValidationResultKind.Invalid;

	const hasNameValidationError = attachable
		.map(
			x => {
				return x.for("name")
					.validation
					.isValid();
			}
		)
		.some(isValid => !isValid);

	const renderContent = (): ReactElement => {
		const trackingId: string = "linked-documents-list";

		if (isMobile) {
			return (
				<div
					{...setTrackingId(trackingId)}
					className={styles.list}
				>
					{attachable.map(
						(itemHandler, index): ReactElement => {
							const {
								value
							} = itemHandler;

							const id: string = getLinkedDocumentId(value);

							return (
								<div
									key={id}
									className={styles.item}
								>
									<ListItem
										attachable={itemHandler}
										onRemove={() => removeHandler(index)}
										onClick={() => onOpenDocument(value)}
										isWebDavAvailable={isWebDavAvailable}
										isSharepointEnabled={isSharepointEnabled}
										isTouch={isTouch}
									/>
								</div>
							);
						}
					)}
				</div>
			);
		}

		return (
			<HubGridViewLayout.List trackingId={trackingId}>
				{attachable.map((itemHandler, index) => {
					const {
						value
					} = itemHandler;

					const id: string = getLinkedDocumentId(value);

					return (
						<HubGridViewLayout.ListItem
							key={id}
						>
							<ListItem
								attachable={itemHandler}
								onRemove={() => removeHandler(index)}
								onClick={() => onOpenDocument(value)}
								isWebDavAvailable={isWebDavAvailable}
								isSharepointEnabled={isSharepointEnabled}
								isTouch={isTouch}
							/>
						</HubGridViewLayout.ListItem>
					);
				})}
			</HubGridViewLayout.List>
		);
	};

	return (
		<>
			{renderContent()}
			{hasError && (
				<ValidationError
					message={
						hasNameValidationError
							? localize("_LinkedDocumentsNameErrorMessage")
							: localize("_SomeLinksAreNotWorkingAnymore")
					}
					trackingId={
						attachable
							.attachToValidationError()
							.trackingId
					}
				/>
			)}
		</>
	);
};
CommunityPostComposerLinkedDocuments.displayName = "CommunityPostComposerLinkedDocuments";

type ListItemProps = {
	attachable: IValueInputAttachable<LinkedDocumentFormData>;
	isWebDavAvailable: boolean;
	isSharepointEnabled: boolean;
	isTouch: boolean;
	onRemove: () => void;
	onClick: () => void;
};

const ListItem = (props: ListItemProps): ReactElement => {
	const {
		attachable,
		isWebDavAvailable,
		isSharepointEnabled,
		isTouch,
		onRemove,
		onClick
	} = props;

	const actions = useMemo(
		(): readonly Action<undefined>[] => getActions(
			onRemove
		),
		[
			onRemove
		]
	);

	const hasError = useMemo(
		(): boolean => attachable.validation.getValidationState().kind === InputValidationResultKind.Invalid,
		[attachable.validation]
	);

	return (
		<LinkedDocumentItem
			model={attachable.value}
			actionContext={undefined}
			actions={actions}
			isSharepointEnabled={isSharepointEnabled}
			isWebDavAvailable={isWebDavAvailable}
			hasError={hasError}
			isTouch={isTouch}
			onClick={onClick}
		/>
	);
};
ListItem.displayName = "ListItem";

function getActions(
	onRemove: () => void
): readonly Action<undefined>[] {
	return ([
		{
			kind: ActionKind.Regular,
			name: "remove-attachment",
			title: localize("_RemoveAttachment"),
			canExecute: _ => true,
			execute: _ => onRemove(),
			icon: "trash-can"
		}
	]);
};
